// src/ConfigManagement.jsx
import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import ConfigEditor from './ConfigEditor';
import { configService } from '../services/config.service';

const ConfigManagement = () => {
  const [configs, setConfigs] = useState({ system: [], policy: [] });
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchConfigs = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const response = await configService.getAllPolicyConfigs();
        
        if (response && response.configs) {
          setConfigs(response.configs);
        } else {
          throw new Error('Invalid response format from API');
        }
      } catch (err) {
        console.error('Failed to fetch configs:', err);
        setError(err.message || 'Failed to load configurations');
      } finally {
        setLoading(false);
      }
    };

    fetchConfigs();
  }, []);

  const renderConfigGroup = (category) => {
    const configList = configs[category] || [];
    return (
      <Box key={category} mb={3}>
        <Typography 
          variant="h6" 
          sx={{ 
            textTransform: 'capitalize', 
            mb: 2,
            color: 'primary.main'
          }}
        >
          {category} Configurations
        </Typography>
        {configList.length > 0 ? (
          configList.map((cfg) => (
            <Paper
              key={cfg.name}
              onClick={() => setSelectedConfig(cfg)}
              elevation={selectedConfig?.name === cfg.name ? 3 : 1}
              sx={{
                p: 2,
                mb: 1,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  elevation: 2
                },
                backgroundColor: selectedConfig?.name === cfg.name 
                  ? 'rgba(25, 118, 210, 0.08)'
                  : 'background.paper'
              }}
            >
              <Typography variant="subtitle1" fontWeight="medium">
                {cfg.name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
              </Typography>
              {cfg.description && (
                <Typography variant="body2" color="text.secondary">
                  {cfg.description}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block">
                Last modified: {new Date(cfg.last_modified).toLocaleString()}
              </Typography>
            </Paper>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            No {category} configurations available
          </Typography>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Error loading configurations: {error}
      </Alert>
    );
  }

  return (
    <Box 
      display="flex" 
      sx={{ 
        height: 'calc(100vh - 200px)',
        backgroundColor: 'background.default',
        borderRadius: 1,
        overflow: 'hidden',
        border: 1,
        borderColor: 'divider'
      }}
    >
      {/* Sidebar */}
      <Box
        width="350px"
        sx={{
          borderRight: 1,
          borderColor: 'divider',
          p: 2,
          overflowY: 'auto',
          backgroundColor: 'background.paper'
        }}
      >
        {renderConfigGroup('system')}
        {renderConfigGroup('policy')}
      </Box>

      {/* Main Panel */}
      <Box 
        flexGrow={1} 
        sx={{ 
          p: 3,
          overflowY: 'auto',
          backgroundColor: 'background.default'
        }}
      >
        {selectedConfig ? (
          <ConfigEditor config={selectedConfig} />
        ) : (
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center" 
            height="100%"
          >
            <Typography variant="body1" color="text.secondary">
              Select a configuration to edit
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ConfigManagement;
